import styled from "styled-components/macro"
import * as Ct from "ldlj"
import { useIntl } from "react-intl"
import { useDispatch } from "react-redux"

import { Option, Select } from "../Commons/Select"
import { useRNBSelector } from "../../store/rootReducer"
import {
  isOptionEnabled,
  LINES_TO_ADD,
  LineToAdd,
  WritingLinePayload,
} from "../../utils/inputMask"
import {
  createLineAction,
  defaultAccounts,
} from "../../store/ducks/inputMask.ducks"
import { GroupOfLines } from "./GroupOfLines"

export const WritingLines = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { editedWritingLines, buyOrSell, possibleAccounts } = useRNBSelector(
    (state) => ({
      editedWritingLines: state.inputMask.edited_writing_lines,
      buyOrSell: state.inputMask.initial_full_document?.buy_or_sell,
      possibleAccounts: state.inputMask.all_company_accounts
        ? state.inputMask.all_company_accounts[
            state.inputMask.initial_full_document?.buy_or_sell || "buy"
          ]
        : defaultAccounts,
    })
  )

  const addWritingLineOptions: Option<LineToAdd>[] = LINES_TO_ADD.map((l) => ({
    value: l,
    label: intl.formatMessage({ id: `input-mask.select-add.options.${l}` }),
    disabled: !isOptionEnabled({
      option: l,
      buyOrSell,
      editedWritingLines,
      possibleAccounts,
    }),
  }))

  const groupedWritingLines = editedWritingLines.reduce<WritingLinePayload[][]>(
    (groups: WritingLinePayload[][], line: WritingLinePayload) => {
      const lastGroup = groups[groups.length - 1]
      if (
        lastGroup.some(
          (l: WritingLinePayload) =>
            l.writing_line_uuid === line.writing_line_uuid
        )
      ) {
        let groupToChange = groups.pop() || []
        const returnValue = [...groups, [...groupToChange, line]]
        return returnValue
      }
      const returnValue = [...groups, [line]]
      return returnValue
    },
    [[]]
  )

  return (
    <WritingLinesWrapper>
      <StyledTableHeader>
        <SizedContent>
          <Select
            height="5rem"
            maxwidth="20rem"
            intl={intl}
            value={null}
            defaultValue={null}
            options={addWritingLineOptions}
            onChangeCallback={(e) => {
              dispatch(
                createLineAction({
                  selectedOption: e.value as LineToAdd,
                  possibleAccounts,
                })
              )
            }}
            label={"comptaSoftware"}
            domain={"input-mask"}
            optionType={"add-line"}
          />
        </SizedContent>
        <SizedTitle
          text={intl.formatMessage({
            id: `input-mask.table-headers.account`,
          })}
          textStyle={{
            fontWeight: 700,
            fontSize: 1.75,
            textTransform: "uppercase",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
        />
        <SizedTitle
          text={intl.formatMessage({
            id: `input-mask.table-headers.debit`,
          })}
          textStyle={{
            fontWeight: 700,
            fontSize: 1.75,
            textTransform: "uppercase",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
          maxWidth={"30rem"}
        />
        <SizedTitle
          text={intl.formatMessage({
            id: `input-mask.table-headers.credit`,
          })}
          textStyle={{
            fontWeight: 700,
            fontSize: 1.75,
            textTransform: "uppercase",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
          maxWidth={"30rem"}
        />
      </StyledTableHeader>
      {groupedWritingLines.map((group, index) => (
        <GroupOfLines
          key={group[0]?.writing_line_uuid}
          group={group}
          isLast={index === groupedWritingLines.length - 1}
        />
      ))}
    </WritingLinesWrapper>
  )
}

const WritingLinesWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  box-shadow: 0px 4px 11px 7px #e5e5e540;
  border-radius: 1rem;
  align-self: flex-start;
`

const SizedTitle = styled(Ct.StyledTitles)<{
  maxWidth?: string
}>`
  flex: 1;
  max-width: ${({ maxWidth }) => (maxWidth ? maxWidth : "auto")};
`
const SizedContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 24rem;
`

const StyledTableHeader = styled(Ct.TableHeader)`
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
`
